"use client"

import {
  BarChart3,
  Bell,
  BookOpenCheck,
  Cable,
  FileClock,
  GitPullRequest,
  Home,
  Inbox,
  Menu,
  PanelLeftClose,
  Search,
  Settings,
  ShieldCheck,
  X,
} from "lucide-react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import type { ReactNode } from "react"
import { useEffect, useRef, useState } from "react"
import { CommandPalette } from "./command-palette"
import { ResoltoWordmark } from "./resolto-brand"
import { ThemeToggle } from "./theme-toggle"

const primaryNav = [
  { href: "/app", label: "Overview", icon: Home, exact: true },
  { href: "/app/cases", label: "Cases", icon: Inbox },
  { href: "/app/approvals", label: "Approvals", icon: ShieldCheck, count: 2 },
  { href: "/app/playbooks", label: "Playbooks", icon: BookOpenCheck },
  { href: "/app/connectors", label: "Connectors", icon: Cable },
  { href: "/app/code-proposals", label: "Code proposals", icon: GitPullRequest, count: 1 },
]

const secondaryNav = [
  { href: "/app/analytics", label: "Analytics", icon: BarChart3 },
  { href: "/app/audit", label: "Audit", icon: FileClock },
  { href: "/app/settings", label: "Settings", icon: Settings },
]

function NavItems({ onNavigate }: { onNavigate?: () => void }) {
  const pathname = usePathname()
  const renderLink = (item: (typeof primaryNav)[number]) => {
    const active = item.exact ? pathname === item.href : pathname.startsWith(item.href)
    const Icon = item.icon
    return (
      <li key={item.href}>
        <Link
          href={item.href}
          aria-current={active ? "page" : undefined}
          {...(onNavigate ? { onClick: onNavigate } : {})}
        >
          <Icon aria-hidden="true" />
          <span>{item.label}</span>
          {"count" in item && item.count ? <span className="nav-count">{item.count}</span> : null}
        </Link>
      </li>
    )
  }

  return (
    <>
      <nav className="app-nav" aria-label="Workspace navigation">
        <ul>{primaryNav.map(renderLink)}</ul>
      </nav>
      <nav className="app-nav app-nav-secondary" aria-label="Workspace administration">
        <ul>{secondaryNav.map(renderLink)}</ul>
      </nav>
    </>
  )
}

export function AppShell({ children }: { children: ReactNode }) {
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const mobileDialogRef = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLocaleLowerCase() === "k") {
        event.preventDefault()
        setPaletteOpen((current) => !current)
      }
    }
    window.addEventListener("keydown", listener)
    return () => window.removeEventListener("keydown", listener)
  }, [])

  return (
    <div className={`app-frame${sidebarCollapsed ? " sidebar-collapsed" : ""}`}>
      <aside className="desktop-sidebar">
        <div className="sidebar-brand-row">
          <ResoltoWordmark href="/app" compact={sidebarCollapsed} />
          <button
            type="button"
            className="icon-button sidebar-collapse"
            onClick={() => setSidebarCollapsed((current) => !current)}
            aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <PanelLeftClose aria-hidden="true" />
          </button>
        </div>
        <div className="workspace-switcher">
          <span className="workspace-avatar">N</span>
          <span>
            <strong>Northstar SaaS</strong>
            <small>Demo workspace</small>
          </span>
        </div>
        <NavItems />
        <div className="sidebar-account">
          <span className="avatar">MC</span>
          <span>
            <strong>Maya Chen</strong>
            <small>Workspace owner</small>
          </span>
        </div>
      </aside>

      <div className="app-main">
        <header className="app-topbar">
          <button
            type="button"
            className="icon-button mobile-menu-button"
            onClick={() => mobileDialogRef.current?.showModal()}
            aria-label="Open workspace navigation"
          >
            <Menu aria-hidden="true" />
          </button>
          <button
            type="button"
            className="command-trigger"
            onClick={() => setPaletteOpen(true)}
            aria-label="Search or jump to"
          >
            <Search aria-hidden="true" />
            <span>Search or jump to…</span>
            <kbd>⌘ K</kbd>
          </button>
          <div className="topbar-actions">
            <span className="demo-label app-demo-label">Demo data</span>
            <ThemeToggle />
            <details className="topbar-notifications">
              <summary className="icon-button" aria-label="Open notifications">
                <Bell aria-hidden="true" />
                <span className="notification-dot" aria-hidden="true" />
              </summary>
              <div className="notification-popover">
                <strong>2 items need attention</strong>
                <Link href="/app/approvals">Two approval requests are waiting</Link>
                <Link href="/app/connectors">Review the degraded Sentry demo adapter</Link>
              </div>
            </details>
          </div>
        </header>
        <div className="app-content">{children}</div>
      </div>

      <dialog
        ref={mobileDialogRef}
        className="mobile-nav-dialog"
        aria-label="Workspace navigation"
        onClick={(event) => {
          if (event.target === mobileDialogRef.current) mobileDialogRef.current?.close()
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") mobileDialogRef.current?.close()
        }}
      >
        <div className="mobile-nav-panel">
          <div className="mobile-nav-heading">
            <ResoltoWordmark href="/app" />
            <button
              type="button"
              className="icon-button"
              onClick={() => mobileDialogRef.current?.close()}
              aria-label="Close navigation"
            >
              <X aria-hidden="true" />
            </button>
          </div>
          <div className="workspace-switcher">
            <span className="workspace-avatar">N</span>
            <span>
              <strong>Northstar SaaS</strong>
              <small>Demo workspace</small>
            </span>
          </div>
          <NavItems onNavigate={() => mobileDialogRef.current?.close()} />
        </div>
      </dialog>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  )
}
